// AUTO-GENERATED shared prelude for packages/shared/src/index.ts — symbols used by multiple chunks.

import { MessageActivityEvent } from "./todo-list-from-tool-event.js";
import { MessageVersion } from "./todo-list-from-tool-event.js";
import type { SubagentRunView } from "./project-member.js";

export type ThemePreference = "light" | "dark" | "system";
export type LanguagePreference = "en" | "fr";
export const THINKING_LEVELS = [
  "none",
  "low",
  "medium",
  "high",
  "max",
] as const;
export type ThinkingLevel = (typeof THINKING_LEVELS)[number];
export function isThinkingLevel(value: unknown): value is ThinkingLevel {
  return THINKING_LEVELS.some((level) => level === value);
}
export type ActivityGeneration =
  | "image"
  | "audio"
  | "video"
  | "speech"
  | "transcription"
  | "model3d"
  | "sprite"
  | "tileset";
export interface ActivityGenerationSettings {
  modelId?: string;
  voice?: string;
  format?: string;
  language?: string;
  size?: string;
  quality?: string;
  aspectRatio?: string;
  duration?: number;
  fps?: number;
  seed?: number;
  temperature?: number;
  topP?: number;
  maxTokens?: number;
  outputFormat?: string;
  engine?: "unity" | "unreal" | "godot" | "web";
  topology?: "triangle" | "quad";
  targetPolycount?: number;
  poseMode?: "a-pose" | "t-pose";
  pbr?: boolean;
  textureResolution?: "2k" | "4k" | "8k";
  texturePrompt?: string;
  negativePrompt?: string;
  modelSeed?: number;
  imageSeed?: number;
  textureSeed?: number;
  texture?: boolean;
  symmetryMode?: "off" | "auto" | "on";
  shouldRemesh?: boolean;
  textureQuality?: "standard" | "detailed" | "extreme";
  geometryQuality?: "standard" | "detailed";
  autoSize?: boolean;
  smartLowPoly?: boolean;
  generateParts?: boolean;
  compress?: "geometry";
  exportUv?: boolean;
  imageAutofix?: boolean;
  textureAlignment?: "original_image" | "geometry";
  orientation?: "default" | "align_image";
  textureFormat?:
    "BMP" | "DPX" | "HDR" | "JPEG" | "PNG" | "TARGA" | "TIFF" | "WEBP";
  bake?: boolean;
  packUv?: boolean;
  exportVertexColors?: boolean;
  pivotToCenterBottom?: boolean;
  scaleFactor?: number;
  withAnimation?: boolean;
  flattenBottom?: boolean;
  flattenBottomThreshold?: number;
  forceSymmetry?: boolean;
  exportOrientation?: "+x" | "-x" | "-y" | "+y";
  fbxPreset?: "blender" | "3dsmax" | "mixamo";
  partNames?: string;
  rig?: boolean;
  rigType?:
    | "biped"
    | "quadruped"
    | "hexapod"
    | "octopod"
    | "avian"
    | "serpentine"
    | "aquatic";
  rigSpec?: "tripo" | "mixamo";
  rigModel?: "v1.0-20240301" | "v2.5-20260210";
  animationActionId?: number;
  animationPreset?: string;
  animationPresets?: string[];
  inputRigged?: boolean;
  bakeAnimation?: boolean;
  exportWithGeometry?: boolean;
  animateInPlace?: boolean;
  collision?: "none" | "box" | "capsule" | "mesh";
  lod?: boolean;
  columns?: number;
  rows?: number;
  tileSize?: number;
  transparentBackground?: boolean;
  scenarioParameters?: Record<string, unknown>;
  /** Raw model-specific parameters for providers such as WaveSpeedAI. */
  waveSpeedParameters?: Record<string, unknown>;
}
export interface GenerationParameters {
  activity?:
    | "ask"
    | "image"
    | "audio"
    | "video"
    | "speech"
    | "transcription"
    | "model3d"
    | "sprite"
    | "tileset";
  /** Models chosen for delegated non-chat activities while using Ask mode. */
  activityModelIds?: Partial<
    Record<
      | "image"
      | "audio"
      | "video"
      | "speech"
      | "transcription"
      | "model3d"
      | "sprite"
      | "tileset",
      string
    >
  >;
  /** Activities the agent is allowed to delegate while using Ask mode. */
  enabledActivities?: Array<
    | "image"
    | "audio"
    | "video"
    | "speech"
    | "transcription"
    | "model3d"
    | "sprite"
    | "tileset"
  >;
  activitySettings?: Partial<
    Record<ActivityGeneration, ActivityGenerationSettings>
  >;
  /** Per-message parameters for the currently selected activity/model. */
  activityParameters?: Partial<
    Record<ActivityGeneration, Record<string, unknown>>
  >;
  goal?: boolean;
  voice?: string;
  format?: string;
  temperature?: number;
  topP?: number;
  maxTokens?: number;
  frequencyPenalty?: number;
  presencePenalty?: number;
  seed?: number;
}
export type Permission =
  | "site:providers:read"
  | "site:providers:write"
  | "site:models:read"
  | "site:models:write"
  | "site:tools:read"
  | "site:tools:write"
  | "site:agent-workspaces:manage"
  | "site:logs:read"
  /** @deprecated Use site:providers:read. Kept only for migration of old rows. */
  | "providers:read"
  /** @deprecated Use site:providers:write. Kept only for migration of old rows. */
  | "providers:write"
  | "providers:personal"
  | "models:read"
  /** @deprecated Use site:models:write. Kept only for migration of old rows. */
  | "models:write"
  /** @deprecated Use site:tools:read. Kept only for migration of old rows. */
  | "tools:read"
  /** @deprecated Use site:tools:write. Kept only for migration of old rows. */
  | "tools:write"
  | "tools:use"
  | "skills:write"
  | "mcp:manage"
  | "site:mcp:manage"
  /** @deprecated Use site:agent-workspaces:manage. Kept only for migration of old rows. */
  | "agent-workspaces:manage"
  | "agent-workspaces:use"
  | "site:git:unrestricted"
  | "git-repositories:unrestricted"
  | "users:read"
  | "users:write"
  | "usage:read"
  | "billing:read"
  | "billing:manage"
  | "audit:read"
  | "organization:manage"
  | "organization:auth:manage"
  | "policies:manage"
  /** @deprecated Use site:logs:read. Kept only for migration of old rows. */
  | "logs:read"
  | "automations:use"
  | "automations:manage"
  | "integrations:use"
  | "integrations:manage"
  | "projects:manage"
  | "chat:use"
  | "site:organizations:read"
  | "site:organizations:manage"
  | "site:users:manage"
  | "site:users:read"
  | "site:organization-users:manage"
  | "site:organization-access:manage"
  | "site:organization-billing:manage"
  | "site:organization-auth:manage"
  | "site:pricing:manage"
  | "site:agents:manage"
  | "site:skills:manage"
  | "site:usage:read"
  | "site:settings:manage";

export type SitePermission = Extract<
  Permission,
  | "providers:read"
  | "providers:write"
  | "tools:read"
  | "tools:write"
  | "site:mcp:manage"
  | "agent-workspaces:manage"
  | "logs:read"
  | "site:providers:read"
  | "site:providers:write"
  | "site:models:read"
  | "site:models:write"
  | "site:tools:read"
  | "site:tools:write"
  | "site:agent-workspaces:manage"
  | "site:logs:read"
  | `site:${string}`
>;

export type OrganizationPermission = Exclude<Permission, SitePermission>;

export type OrganizationRole =
  | "owner"
  | "admin"
  | "automation_manager"
  | "connection_manager"
  | "billing_admin"
  | "member";

export type OrganizationCapability =
  | "personalProviders"
  | "organizationProviders"
  | "personalWorkspaces"
  | "sshWorkspaces"
  | "unrestrictedSshHosts"
  | "privateNetwork"
  | "customProviderEndpoints"
  | "unrestrictedProviderHosts"
  | "unrestrictedOutboundHosts"
  | "customWebhooks"
  | "customerManagedConnectors"
  | "allowInternetEgress";

export type OrganizationCapabilities = Record<OrganizationCapability, boolean>;

export interface AutomationMessageInput {
  content: string;
  attachmentIds: string[];
  artifactIds: string[];
  toolIds: string[];
  skillIds: string[];
  mcpServerIds: string[];
  mcpToolSelections: Record<string, string[]>;
  agentWorkspaceId: string | null;
  agentWorkspaceIds?: string[];
  repositoryIds?: Record<string, string[]>;
  repositoryId: string | null;
  agentId: string | null;
  thinkingLevel?: ThinkingLevel;
  generationParameters?: GenerationParameters;
}

export interface Automation {
  id: string;
  name: string;
  prompt: string;
  ownerUserId: string;
  visibility: "personal" | "organization";
  lifecycle: "draft" | "published" | "paused" | "archived";
  runAs: "owner" | "organization_service";
  version: number;
  maxConcurrentRuns: number;
  timeoutSeconds: number;
  retryLimit: number;
  monthlySpendLimitMicros: number | null;
  scheduleType: "cron" | "once";
  cronExpression: string | null;
  runAt: string | null;
  timezone: string;
  enabled: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  modelId: string | null;
  agentId: string | null;
  messageInput: AutomationMessageInput;
  createdAt: string;
  updatedAt: string;
}

export interface AutomationRun {
  id: string;
  automationId: string;
  conversationId: string | null;
  status: "queued" | "running" | "completed" | "failed" | "cancelled";
  scheduledFor: string;
  startedAt: string | null;
  finishedAt: string | null;
  cancelledAt: string | null;
  error: string | null;
  createdAt: string;
  automationName?: string;
  eventTriggerId?: string | null;
  eventId?: string | null;
}

export interface Integration {
  id: string;
  name: string;
  kind: IntegrationKind;
  scope: "personal" | "organization";
  ownerUserId: string | null;
  emailAddress?: string | null;
  folder?: string;
  address?: string | null;
  providerAccountId?: string | null;
  webhookPath?: string | null;
  capabilities?: IntegrationCapability[];
  enabled: boolean;
  lastPolledAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export type IntegrationKind =
  | "mailbox"
  | "whatsapp"
  | "telegram"
  | "slack"
  | "teams"
  | "sms"
  | "voice"
  | "webhook"
  | "google_calendar"
  | "microsoft_calendar"
  | "google_drive"
  | "webdav"
  | "nextcloud"
  | "samba"
  | "onedrive"
  | "dropbox"
  | "box"
  | "s3"
  | "sharepoint"
  | "notion"
  | "hubspot"
  | "zendesk"
  | "github"
  | "gitlab"
  | "codeberg"
  | "gitea"
  | "google_play_console"
  | "app_store_connect"
  | "facebook_pages"
  | "instagram_business"
  | "linkedin"
  | "tiktok"
  | "x"
  | "youtube"
  | "email_marketing"
  | "google_analytics"
  | "google_search_console"
  | "restolution"
  | "lightspeed_restaurant"
  | "deliverect"
  | "thefork"
  | "zenchef"
  | "wolt_drive"
  | "netvisor"
  | "billit"
  | "paytrail"
  | "payconiq"
  | "bancontact"
  | "google_business_profile"
  | "sd_worx"
  | "acerta"
  | "partena_professional"
  | "securex"
  | "liantis"
  | "ytj_prh"
  | "cbe_kbo"
  | "vies"
  | "gs1_food_data"
  | "kespro"
  | "procountor"
  | "fennoa"
  | "mollie"
  | "odoo"
  | "tripadvisor"
  | "tableonline"
  | "resdiary"
  | "just_eat"
  | "foodora"
  | "peppol_gateway"
  | "stripe"
  | "sumup"
  | "mobilepay"
  | "klarna"
  | "uber_eats"
  | "deliveroo"
  | "bolt_food"
  | "resq_club"
  | "opentable"
  | "tablebooker"
  | "resengo"
  | "visma_eaccounting"
  | "exact_online"
  | "teamleader"
  | "clearfacts"
  | "doccle"
  | "unifiedpost"
  | "palkka_fi"
  | "sympa"
  | "metro_tukku"
  | "meira_nova"
  | "ponto"
  | "fivaldi"
  | "etasku"
  | "mepco"
  | "palkkaus_fi"
  | "ucm"
  | "yuki"
  | "octopus"
  | "horus"
  | "winbooks"
  | "worldline"
  | "nets_nexi"
  | "e_invoice_be";

export type IntegrationCapability =
  | "receive_events"
  | "send_messages"
  | "send_media"
  | "calendar"
  | "files"
  | "crm"
  | "tickets"
  | "repositories"
  | "social"
  | "marketing_email"
  | "analytics"
  | "leads"
  | "pos"
  | "orders"
  | "reservations"
  | "delivery"
  | "accounting"
  | "e_invoicing"
  | "payments"
  | "payroll"
  | "reviews"
  | "business_registry"
  | "food_data"
  | "procurement";

export interface AutomationEventTrigger {
  id: string;
  automationId: string;
  integrationId: string;
  eventType:
    | "message_received"
    | "order_received"
    | "reservation_created"
    | "payment_updated"
    | "invoice_received"
    | "review_received"
    | "delivery_updated"
    | "payroll_updated"
    | "procurement_updated"
    | "lead_received"
    | "comment_received"
    | "mention_received"
    | "metric_alert";
  filters: {
    senderContains?: string;
    subjectContains?: string;
    bodyContains?: string;
    folder?: string;
  };
  conversationMode: "thread" | "event";
  responseMode: "none" | "full_response" | "tool";
  enabled: boolean;
  integration?: Integration;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string | null;
  language: LanguagePreference | null;
  defaultLanguage: LanguagePreference;
  memoryEnabled: boolean;
  organizationRole: OrganizationRole;
  permissions: Permission[];
  organizationPermissions: OrganizationPermission[];
  sitePermissions: SitePermission[];
  capabilities: OrganizationCapabilities;
  supportAccess?: boolean;
}

export interface UserMemory {
  id: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface ModelPreferences {
  defaultModelId: string | null;
  adminDefaultModelId: string | null;
  favoriteModelIds: string[];
  /** Per-mode default models (ask, image, audio, video, speech, transcription). */
  activityDefaultModelIds?: Partial<
    Record<"ask" | ActivityGeneration, string | null>
  >;
}

export interface ModelCapabilities {
  vision: boolean;
  imageGeneration: boolean;
  tools: boolean;
}

export interface RemoteModelMetadata {
  canonicalSlug: string | null;
  contextLength: number | null;
  architecture: Record<string, unknown> | null;
  pricing: Record<string, unknown> | null;
  supportedParameters: string[];
  providerMetadata: Record<string, unknown> | null;
  remoteMetadata: Record<string, unknown>;
}

export interface Model extends RemoteModelMetadata {
  /** Provider-declared choices; absent preserves the legacy three levels. */
  thinkingLevels?: ThinkingLevel[];
  id: string;
  providerId: string;
  providerName?: string;
  remoteId: string;
  name: string;
  description: string | null;
  icon: string | null;
  tags: string[];
  enabled: boolean;
  userEnabled?: boolean;
  sortOrder: number;
  /** Whether tool calls are enabled by an administrator. Independent from capability support. */
  toolsEnabled: boolean;
  capabilities: ModelCapabilities;
  scope?: "platform" | "personal";
}

export interface CodexCreditsSnapshot {
  hasCredits?: boolean | null;
  unlimited?: boolean | null;
  balance?: number | null;
  usedPercent?: number | null;
  windowDurationMins?: number | null;
  resetsAt?: number | null;
}

export interface CodexRateLimitWindow {
  limitId?: string | null;
  limitName?: string | null;
  primary?: CodexCreditsSnapshot | null;
  secondary?: CodexCreditsSnapshot | null;
  credits?: CodexCreditsSnapshot | null;
  usedPercent?: number | null;
  windowDurationMins?: number | null;
  resetsAt?: number | null;
  spendControlReached?: boolean | null;
  rateLimitReachedType?: string | null;
}

export interface CodexDailyUsageBucket {
  startDate: string | null;
  tokens: number | null;
}

export interface CodexTokenUsage {
  lifetimeTokens?: number | null;
  peakDailyTokens?: number | null;
  currentStreakDays?: number | null;
  longestStreakDays?: number | null;
  dailyUsageBuckets?: CodexDailyUsageBucket[];
}

export interface CodexSubscriptionUsage {
  connected: boolean;
  email?: string | null;
  planType?: string | null;
  credits?: CodexCreditsSnapshot | null;
  rateLimits?: CodexRateLimitWindow[] | null;
  rateLimitResetCredits?: {
    availableCount?: number | null;
    details?: Record<string, unknown>[] | null;
  } | null;
  tokenUsage?: CodexTokenUsage | null;
  unavailable?: string | null;
}

export interface OpenCodeUsageWindow {
  status?: string | null;
  resetInSec?: number | null;
  usagePercent?: number | null;
}

export interface OpenCodeUsage {
  useBalance?: boolean | null;
  rollingUsage?: OpenCodeUsageWindow | null;
  weeklyUsage?: OpenCodeUsageWindow | null;
  monthlyUsage?: OpenCodeUsageWindow | null;
  unavailable?: string | null;
}

export interface Provider {
  id: string;
  name: string;
  type:
    | "openai-compatible"
    | "openrouter"
    | "ollama"
    | "anthropic"
    | "mistral"
    | "opencode-go"
    | "opencode-zen"
    | "codex-subscription"
    | "claude-code-subscription"
    | "claude-agent"
    | "commandcode"
    | "meshy"
    | "scenario"
    | "layer"
    | "rodin"
    | "tripo"
    | "wavespeed"
    | "retro-diffusion"
    | "custom";
  baseUrl: string;
  apiKeyConfigured: boolean;
  customProviderId?: string;
  customProviderDisplayName?: string;
  /**
   * Non-secret per-instance values for a custom provider definition's
   * inputSchema. Secret values are never returned to the browser.
   */
  instanceConfig?: Record<string, unknown>;
  /**
   * True when any instance-config value (including secret fields) is stored
   * for this provider, so the editor can offer "leave blank to keep" for
   * secret inputs on edit.
   */
  instanceConfigConfigured?: boolean;
  codexSubscription?: {
    connected: boolean;
    email?: string | null;
    planType?: string | null;
  };
  modelPolicy: "all" | "regex" | "selected";
  modelRegex: string | null;
  selectedModels?: string[];
  filterCapabilities?: (keyof ModelCapabilities)[];
  filterOutputModalities?: string[];
  enabled: boolean;
  scope?: "platform" | "organization" | "personal";
  models?: Model[];
}

/**
 * A reusable provider definition that backs providers of type "custom".
 * The definition carries the adapter script and schemas; providers reference
 * it through `customProviderId`.
 */
export interface CustomProviderDefinition {
  id: string;
  name: string;
  displayName: string;
  description: string;
  scope: "platform" | "organization" | "personal";
  runtime: "process" | "docker";
  containerImage?: string | null;
  baseUrl?: string | null;
  enabled: boolean;
  dependencies?: Record<string, string>;
  inputSchema?: Record<string, unknown>;
  adminSchema?: Record<string, unknown>;
  userSchema?: Record<string, unknown>;
}

export interface Attachment {
  id: string;
  name: string;
  mimeType: string;
  url: string;
  kind: "image" | "file" | "generated-image";
  /** Set for uploaded documents that are available to document_studio. */
  artifactId?: string;
}

export interface Artifact {
  id: string;
  saved: boolean;
  name: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  updatedAt: string;
  url: string;
  downloadUrl: string;
  reference: string;
}

/** One recorded completion against a message (a message can have several when it was continued or retried). */
export interface MessageUsageEvent {
  id: string;
  kind: string;
  modelName: string;
  modelRemoteId: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  costMicros: number;
  currency: string;
  createdAt: string;
}

export interface Message {
  id: string;
  conversationId: string;
  parentId: string | null;
  versionGroupId: string;
  versionIndex: number;
  versionCount?: number;
  versions?: MessageVersion[];
  role: "user" | "assistant" | "system" | "tool";
  content: string;
  attachments: Attachment[];
  artifactIds?: string[];
  createdAt: string;
  modelId?: string | null;
  modelName?: string | null;
  modelRemoteId?: string | null;
  error?: string | null;
  generationStatus?:
    | "running"
    | "awaiting-approval"
    | "awaiting-question"
    | "completed"
    | "failed"
    | "cancelled";
  /** Lightweight persisted presentation; full activity remains available separately. */
  displaySummary?: MessageDisplaySummary | null;
  activityDeferred?: boolean;
  activityMessageIds?: string[];
  reasoning?: string | null;
  toolEvents?: ToolEvent[];
  activityEvents?: MessageActivityEvent[];
  toolIds?: string[];
  skillIds?: string[];
  mcpServerIds?: string[];
  mcpToolSelections?: Record<string, string[]>;
  agentWorkspaceId?: string | null;
  agentWorkspaceIds?: string[];
  repositoryIds?: Record<string, string[]>;
  agentId?: string | null;
  generationParameters?: GenerationParameters;
  subagentRuns?: SubagentRunView[];
  usageEvents?: MessageUsageEvent[];
}

export const CONTEXT_COMPACTION_MARKER =
  "[Automatically compacted conversation context]";

export function isContextCompactionMessage(
  message: Pick<Message, "role" | "content" | "generationStatus">,
) {
  return (
    message.role === "system" &&
    message.content.startsWith(CONTEXT_COMPACTION_MARKER) &&
    message.generationStatus !== "running" &&
    message.generationStatus !== "failed"
  );
}

export interface ToolEvent {
  id: string;
  /** Stable per-execution reference used for scoped full-result retrieval. */
  resultRef?: string;
  name: string;
  displayName?: string;
  workspaceName?: string;
  workspaceSlug?: string;
  repositoryName?: string;
  repositorySlug?: string;
  arguments: unknown;
  result?: unknown;
  /** Exact bounded provider-facing result projection, reused for replay. */
  modelContent?: string;
  /** Original assistant wire message shared by all calls in this provider round. */
  providerMessage?: Record<string, unknown>;
  /** First tool-call ID of the original provider round, preserving batched calls. */
  providerRoundId?: string;
  error?: string;
  approvalId?: string;
  approvalReason?: string;
  questionId?: string;
  status:
    | "running"
    | "awaiting-approval"
    | "awaiting-question"
    | "completed"
    | "failed";
  startedAt?: string;
  finishedAt?: string;
}

export type TodoStatus = "pending" | "in_progress" | "completed";

export interface TodoItem {
  content: string;
  status: TodoStatus;
}

export interface TodoListSnapshot {
  todos: TodoItem[];
  explanation?: string;
  completed: number;
  total: number;
}

export interface MessageDisplaySummary {
  finalContent: string;
  finalTextEventId: string | null;
  hasActivity: boolean;
  todoToolEvents: ToolEvent[];
}
